import React, { Component } from 'react';
import ExportDevice from './views/ExportDevices';
import './App.scss';
import './gstart-console.css';

// 2026-07-18: Firebase login replaced with the unified GroTron email-OTP login
// (fm_lite_auth_signin + fm_lite_auth_verify_otp on NEW_API_2 / REACT_APP_URL_AUTH),
// appType 'gstart' — the same login the g_start Flutter app uses. No API change.
// The verify response's user.uid (grotron_uid) is what the fm_export_* services expect.

const AUTH = process.env.REACT_APP_URL_AUTH;

class App extends Component {
  constructor(props) {
    super(props);
    this.state = {
      uid: null,
      token: null,
      isSignedIn: false,
      isfrommobileapp: false,
      selected_farm: { farm_id: '' },

      // login flow
      step: 'email',        // 'email' | 'otp'
      email: '',
      userEmail: '',        // signed-in user's email (shown in the top bar)
      confirmSignout: false, // sign-out confirmation dialog
      userId: null,         // fm_gt_user.user_id returned by signin
      otp: '',
      loading: false,
      error: '',

      lblComLogin: "Login",
      lblComMsg1: "Agronomy Expertise + AI = Precision Agriculture.",
      lblComMsg2: "Reduce Water Consumption by upto 50%",
      lblComMsg3: "Fertilizer Reduction by upto 30%",
      lblComMsg4: "Boost Yield by upto 50%",
    };
  }

  componentDidMount() {
    // Launched from a mobile app with a uid in the URL -> use it directly.
    const dataQuery = new URLSearchParams(window.location.search);
    const uidParam = dataQuery.get("uid");
    if (uidParam) {
      // Mobile app may also pass the JWT so the (now JWT-enforced) data calls work.
      const tokenParam = dataQuery.get("token");
      if (tokenParam) localStorage.setItem("gstart_token", tokenParam);
      this.setState({ uid: uidParam, token: tokenParam || null, isfrommobileapp: true });
      this.installNavGuard();   // arm immediately, don't wait for a later update
      return;
    }

    // Otherwise restore a previous browser login (survives refresh).
    const savedUid = localStorage.getItem("gstart_uid");
    const savedToken = localStorage.getItem("gstart_token");
    if (savedUid) {
      this.setState({ uid: savedUid, token: savedToken, userEmail: localStorage.getItem("gstart_email") || '', isSignedIn: true });
      this.installNavGuard();   // arm immediately, don't wait for a later update
    }
  }

  // Step 1: send the OTP to the entered email.
  sendOtp = () => {
    const email = this.state.email.trim();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      this.setState({ error: "Please enter a valid email address." });
      return;
    }
    this.setState({ loading: true, error: '' });
    fetch(AUTH + "/fm_lite_auth_signin", {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identifier: email, appType: 'gstart' }),
    })
      .then(res => res.json())
      .then(data => {
        if (data && data.userId) {
          this.setState({ loading: false, step: 'otp', userId: data.userId, error: '' });
        } else {
          this.setState({ loading: false, error: (data && data.message) || "Could not send the code. Please try again." });
        }
      })
      .catch(() => this.setState({ loading: false, error: "Network error. Please try again." }));
  };

  // Step 2: verify the OTP -> get grotron uid + token.
  verifyOtp = () => {
    const otp = this.state.otp.trim();
    if (!/^\d{4}$/.test(otp)) {
      this.setState({ error: "Enter the 4-digit code from your email." });
      return;
    }
    this.setState({ loading: true, error: '' });
    fetch(AUTH + "/fm_lite_auth_verify_otp", {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId: this.state.userId,
        otp: otp,
        otpType: 'signin',
        appType: 'gstart',
        deviceType: 'web',
      }),
    })
      .then(res => res.json())
      .then(data => {
        const uid = data && data.user && (data.user.uid || data.user.userId);
        if (uid) {
          const mail = (data.user && data.user.email) || '';
          localStorage.setItem("gstart_uid", uid);
          if (mail) localStorage.setItem("gstart_email", mail);
          if (data.token) localStorage.setItem("gstart_token", data.token);
          this.setState({ loading: false, isSignedIn: true, uid: uid, userEmail: mail, token: data.token || null, error: '' });
          this.installNavGuard();   // arm the back-guard the moment login succeeds
        } else {
          this.setState({ loading: false, error: (data && data.message) || "Invalid or expired code." });
        }
      })
      .catch(() => this.setState({ loading: false, error: "Network error. Please try again." }));
  };

  // ---- Accidental-exit guard (2026-07-18) ----
  // Single-page console: a stray Back / refresh / tab-close would drop the tech
  // user out. While signed in we attach a beforeunload handler so the browser
  // shows its native "Leave site?" confirm on any real exit attempt (Back that
  // leaves the tab, reload, or close). Native = it can't be suppressed or
  // escaped, and its "Leave" button performs the exit the user asked for.
  // Removed on sign-out / unmount so intentional sign-out doesn't prompt.
  onBeforeUnload = (e) => {
    e.preventDefault();
    e.returnValue = '';   // required for Chrome to show the prompt
    return '';
  };

  installNavGuard = () => {
    if (this._navGuard) return;
    this._navGuard = true;
    window.addEventListener('beforeunload', this.onBeforeUnload);
  };

  removeNavGuard = () => {
    if (!this._navGuard) return;
    this._navGuard = false;
    window.removeEventListener('beforeunload', this.onBeforeUnload);
  };

  componentDidUpdate() {
    if (this.state.isSignedIn || this.state.uid) this.installNavGuard();
    else this.removeNavGuard();
  }

  componentWillUnmount() {
    this.removeNavGuard();
  }

  handleSignOut = () => {
    localStorage.removeItem("gstart_uid");
    localStorage.removeItem("gstart_token");
    localStorage.removeItem("gstart_email");
    this.setState({
      uid: null, token: null, userEmail: '', isSignedIn: false, confirmSignout: false,
      step: 'email', email: '', userId: null, otp: '', error: '', loading: false,
    });
  };

  // App-store badges (self-contained inline SVG icons — no external assets).
  storeBadges() {
    return (
      <div className="gs-login-stores">
        <a className="gs-badge" href="https://apps.apple.com/in/app/gstart/id6784502208"
          target="_blank" rel="noopener noreferrer" aria-label="Download GStart on the App Store">
          <svg viewBox="0 0 384 512" aria-hidden="true"><path fill="currentColor" d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/></svg>
          <span><small>Download on the</small><b>App Store</b></span>
        </a>
        <a className="gs-badge" href="https://play.google.com/store/apps/details?id=in.farmagain.g_start"
          target="_blank" rel="noopener noreferrer" aria-label="Get GStart on Google Play">
          <svg viewBox="0 0 512 512" aria-hidden="true"><path fill="currentColor" d="M325.3 234.3 104.6 13l280.8 161.2-60.1 60.1zM47 0C34 6.8 25.3 19.2 25.3 35.3v441.3c0 16.1 8.7 28.5 21.7 35.3l256.6-256L47 0zm425.2 225.6-58.9-34.1-65.7 64.5 65.7 64.5 60.1-34.1c18-14.3 18-46.5-1.2-60.8zM104.6 499l220.7-221.3 60.1 60.1L104.6 499z"/></svg>
          <span><small>GET IT ON</small><b>Google Play</b></span>
        </a>
      </div>
    );
  }

  renderLogin() {
    const { step, email, otp, loading, error } = this.state;
    return (
      <div className="gs-login">
        <div className="gs-login-card">
          {/* Brand panel (hidden on small screens) */}
          <div className="gs-login-brand">
            <div className="gs-login-logo"><img src="./gstart_logo.png" alt="GStart" /></div>
            <div className="gs-login-brandname">GStart</div>
            <div className="gs-login-tagline">Device Console</div>
            <ul className="gs-login-points">
              <li>Control pumps &amp; valves from anywhere</li>
              <li>Automate day-wise irrigation schedules</li>
              <li>Protect your motor with voltage &amp; current limits</li>
              <li>Track live device status &amp; activity history</li>
            </ul>
            <div className="gs-login-getapp">
              <span className="gs-login-getapp-h">Get the GStart app</span>
              {this.storeBadges()}
            </div>
            <a className="gs-login-web" href="https://www.farmagain.co/" target="_blank" rel="noopener noreferrer">
              farmagain.co ↗
            </a>
          </div>

          {/* Form panel */}
          <div className="gs-login-form">
            <form onSubmit={(e) => { e.preventDefault(); step === 'email' ? this.sendOtp() : this.verifyOtp(); }}>
              <div className="gs-login-mini">
                <img src="./gstart_logo.png" alt="" /> GStart
              </div>
              <h1 className="gs-login-title">{step === 'email' ? 'Sign in' : 'Enter code'}</h1>
              <p className="gs-login-sub">
                {step === 'email'
                  ? 'Use your GroTron email to continue'
                  : <>We sent a 4-digit code to <strong>{email}</strong></>}
              </p>

              {error ? <div className="gs-login-error">{error}</div> : null}

              {step === 'email' ? (
                <label className="gs-login-field">
                  <span>Email</span>
                  <input type="email" placeholder="you@farmagain.in" autoFocus value={email}
                    onChange={(e) => this.setState({ email: e.target.value })} />
                </label>
              ) : (
                <label className="gs-login-field">
                  <span>Verification code</span>
                  <input type="tel" inputMode="numeric" className="gs-login-otp" placeholder="••••"
                    maxLength={4} autoFocus value={otp}
                    onChange={(e) => this.setState({ otp: e.target.value.replace(/\D/g, '') })} />
                </label>
              )}

              <button className="gs-login-btn" type="submit" disabled={loading}>
                {loading ? <span className="gs-login-spin" /> : (step === 'email' ? 'Send code' : 'Verify & sign in')}
              </button>

              {step === 'otp' ? (
                <button className="gs-login-link" type="button" disabled={loading}
                  onClick={() => this.setState({ step: 'email', otp: '', error: '' })}>
                  ← Change email
                </button>
              ) : null}
            </form>

            {/* Shown on small screens (brand panel is hidden there) */}
            <div className="gs-login-mobstore">
              <span className="gs-login-getapp-h">Get the GStart app</span>
              {this.storeBadges()}
              <a className="gs-login-web" href="https://www.farmagain.co/" target="_blank" rel="noopener noreferrer">farmagain.co ↗</a>
            </div>
          </div>
        </div>
      </div>
    );
  }

  render() {
    const { isSignedIn, uid, isfrommobileapp, selected_farm } = this.state;

    // Not logged in and not launched from the mobile app -> show the login screen.
    if (!isSignedIn && !uid) {
      return this.renderLogin();
    }

    const email = this.state.userEmail || '';
    return (
      <div className="gs-console gs-app">
        <div className="gs-topbar">
          <div className="gs-brand">
            <img className="gs-logo" src="./gstart_logo.png" alt="GStart" />
            GStart <small>Device Console</small>
          </div>
          <div className="gs-user">
            {email ? <span className="gs-email">{email}</span> : null}
            <div className="gs-avatar">{(email || 'U').slice(0, 2).toUpperCase()}</div>
            {!isfrommobileapp && (
              <button className="gs-logout" onClick={() => this.setState({ confirmSignout: true })} title="Sign out" aria-label="Sign out">
                <i className="fa fa-power-off"></i>
              </button>
            )}
          </div>
        </div>
        <div className="gs-page">
          <ExportDevice
            uid={uid}
            token={this.state.token}
            selected_farm={selected_farm}
            currentUser={null}
            isfrommobileapp={isfrommobileapp}
            onLogout={this.handleSignOut}
          />
        </div>

        {this.state.confirmSignout && (
          <div className="gs-overlay" onClick={() => this.setState({ confirmSignout: false })}>
            <div className="gs-confirm" onClick={(e) => e.stopPropagation()}>
              <div className="gs-confirm-title">Sign out?</div>
              <div className="gs-confirm-text">You'll need to sign in again with your email to continue.</div>
              <div className="gs-confirm-actions">
                <button className="gs-cbtn gs-btn-ghost" onClick={() => this.setState({ confirmSignout: false })}>Cancel</button>
                <button className="gs-cbtn gs-btn-danger" onClick={this.handleSignOut}>Sign out</button>
              </div>
            </div>
          </div>
        )}

      </div>
    );
  }
}

export default App;
